import { WA_WINDOW } from '@ng-web-apis/common';
import { TuiHeader, TuiCardLarge, TuiSurface } from '@taiga-ui/layout';
import { TuiTabs } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { DeviceDetectorService } from '@core/services';
import { AccountService } from '@core/services/account/account.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestService } from '@core/services/request/request.service';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import { ReactiveFormsModule } from '@angular/forms';
import { TuiLoader, TuiTitle } from '@taiga-ui/core';
import { AppVersionComponent } from '@core/components';
import {
  RequestFormRoutePaths,
  RequestFormTabTitlesTranslateKeys,
} from '@features/requests/request-form/types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, fromEvent, map } from 'rxjs';
import { PdfViewerDialogComponent } from '@core/components/pdf-viewer-dialog';
import { DeviceTamperStoreService } from './services/device-tamper-store.service';
import { FeatureFlagsService } from '@core/services/feature-flags.service';

@Component({
  selector: 'x-request-form',
  imports: [
    RouterOutlet,
    TuiTabs,
    RouterLinkActive,
    RouterLink,
    ReactiveFormsModule,
    TuiLoader,
    TuiCardLarge,
    TuiHeader,
    TuiSurface,
    TuiTitle,
    AppVersionComponent,
    TranslatePipe,
    NgClass,
    PdfViewerDialogComponent,
  ],
  templateUrl: './request-form.component.html',
  styleUrl: './request-form.component.scss',
  providers: [DeviceTamperStoreService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormComponent implements OnInit {
  readonly $requestNumber = input.required<string>({ alias: 'requestNumber' });

  private readonly requestService = inject(RequestService);
  private readonly outkeepPermissionService = inject(OutkeepPermissionService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly accountService = inject(AccountService);
  private readonly workflowService = inject(WorkflowService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WA_WINDOW);
  private readonly featureFlags = inject(FeatureFlagsService);

  readonly RequestFormTabTitlesTranslateKeys =
    RequestFormTabTitlesTranslateKeys;
  public tabs = this.filterTabsByFeature(Object.values(RequestFormRoutePaths));
  public tabIndex = 0;

  readonly $request = this.requestService.$request;
  readonly $isDesktop = toSignal(this.deviceService.isDesktop$);
  public readonly $isWorkflowRoute = signal(false);
  private readonly $urlAfterNavigation = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  public readonly $requestWorkflowsExist =
    this.workflowService.requestWorkflowsExist;

  constructor() {
    effect(() => {
      if (this.$requestNumber()) {
        this.$isWorkflowRoute.set(this.router.url.includes('/workflows'));
        this.requestService.loadRequest(this.$requestNumber());
        if (this.featureFlags.workflowsEnabled) {
          this.workflowService.entities$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      }
    });

    effect(() => {
      const request = this.$request();

      if (request) {
        this.updateTabs();
      }
    });

    effect(() => {
      const url = this.$urlAfterNavigation();

      if (
        this.requestService.$requestOfflineState() === 'snapshot' &&
        !url.endsWith(`/${RequestFormRoutePaths.Summary}`)
      ) {
        this.tabs = [RequestFormRoutePaths.Summary];
        void this.router.navigate([
          '/requests',
          this.$requestNumber(),
          RequestFormRoutePaths.Summary,
        ]);
      }
    });

    fromEvent(this.window, 'online')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.requestService.$requestOfflineState() !== 'online') {
          this.requestService.reloadRequest();
        }
      });
  }

  ngOnInit(): void {
    this.accountService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateTabs();
      });
  }

  private updateTabs(): void {
    const allTabs = this.filterTabsByFeature(
      Object.values(RequestFormRoutePaths),
    );
    const tabsByFields = this.$request()
      ? this.filterTabsByFields(allTabs)
      : allTabs;

    this.tabs = this.filterTabsByPermission(tabsByFields);
  }

  private filterTabsByFeature(
    tabs: RequestFormRoutePaths[],
  ): RequestFormRoutePaths[] {
    return tabs.filter(
      tab =>
        this.featureFlags.workflowsEnabled ||
        tab !== RequestFormRoutePaths.Workflows,
    );
  }

  private filterTabsByPermission(
    tabs: RequestFormRoutePaths[] = this.tabs,
  ): RequestFormRoutePaths[] {
    const routePermissions = new Map<
      RequestFormRoutePaths,
      string | undefined
    >();

    this.activatedRoute.routeConfig?.children?.forEach(route => {
      routePermissions.set(
        route.path as RequestFormRoutePaths,
        route.data?.['permission'],
      );
    });

    return tabs.filter(tab => {
      const permission = routePermissions.get(tab);

      if (permission) {
        return this.outkeepPermissionService.$hasPermission(permission)();
      }

      return true;
    });
  }

  private filterTabsByFields(
    tabs: RequestFormRoutePaths[] = this.tabs,
  ): RequestFormRoutePaths[] {
    const request = this.$request();
    const workflowsExist = this.$requestWorkflowsExist();

    return tabs.filter(
      tab =>
        (request.hasEDO || tab !== RequestFormRoutePaths.Inspection) &&
        (workflowsExist || tab !== RequestFormRoutePaths.Workflows),
    );
  }
}
